import authService from './authService';

export interface Track {
  track_id: string;
  track_name: string;
  track?: string;
  del_row?: boolean;
  batch_count?: number;
  courses_count?: number;
  course_name?: string;
}

const TRACKS_CACHE_KEY = 'tracks';

const isValidTrackList = (value: unknown): value is Track[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (t) =>
      t &&
      typeof t === 'object' &&
      typeof (t as Track).track_id === 'string' &&
      (t as Track).track_id.length > 0,
  );

const readCachedTracks = (): Track[] | null => {
  const raw = sessionStorage.getItem(TRACKS_CACHE_KEY);
  if (!raw || raw === '[]' || raw === 'null' || raw === 'undefined') {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    return isValidTrackList(parsed) ? parsed : null;
  } catch {
    sessionStorage.removeItem(TRACKS_CACHE_KEY);
    return null;
  }
};

/**
 * Fetch all tracks
 * @returns Promise<Track[]> Array of track objects
 */
export const fetchAllTracks = async (): Promise<Track[]> => {
  try {
    const response = await authService.get('admin/get_all_tracks/');
    
    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to fetch tracks');
    }
    
    return response.data.tracks || [];
  } catch (error) {
    console.error('Error fetching tracks:', error);
    throw error;
  }
};

/**
 * Fetch all tracks for courses
 * @returns Promise<Track[]> Array of track objects
 */
export const fetchAllTracksForCourses = async (
  forceRefresh = false,
): Promise<Track[]> => {
  try {
    if (!forceRefresh) {
      const cached = readCachedTracks();
      if (cached) {
        return cached;
      }
    }

    const response = await authService.get('admin/get_all_tracks_for_courses/');

    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to fetch tracks');
    }

    const tracks = response.data?.tracks;
    if (!isValidTrackList(tracks)) {
      sessionStorage.removeItem(TRACKS_CACHE_KEY);
      return Array.isArray(tracks) ? tracks : [];
    }

    sessionStorage.setItem(TRACKS_CACHE_KEY, JSON.stringify(tracks));
    return tracks;
  } catch (error) {
    console.error('Error fetching tracks for courses:', error);
    throw error;
  }
};

